/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import ArgonBadge from "components/ArgonBadge";
import ArgonAvatar from "components/ArgonAvatar";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Components
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import Dropdown from "components/CustomDropdown/Dropdown";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import FileUploader from "components/FileUploader/FileUploader";
import PaginationBar from "components/PaginationBar/PaginationBar";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "@mui/material";

// Services
import { getAllReferrals, deleteReferral, processRedeemRequest } from "services/referralService";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

function Tables() {
  // State management
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentTab, setCurrentTab] = useState("All");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    declined: 0,
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [viewDialog, setViewDialog] = useState(false);
  const [processDialog, setProcessDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Form states for process payment
  const [processStatus, setProcessStatus] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [screenShotFiles, setScreenShotFiles] = useState([]);
  const [processing, setProcessing] = useState(false);

  // Fetch stats separately (only once on mount or tab change)
  const fetchStats = async () => {
    try {
      const response = await getAllReferrals({ limit: 10000 }); // Fetch all for stats
      if (response.success) {
        const allReferrals = response.data;
        const total = response.pagination?.total || allReferrals.length;
        const pending = allReferrals.filter((ref) => ref.status === "PENDING").length;
        const approved = allReferrals.filter((ref) => ref.status === "APPROVED").length;
        const declined = allReferrals.filter((ref) => ref.status === "DECLINED").length;

        setStats({
          total,
          pending,
          approved,
          declined,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Fetch referrals from API
  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: rowsPerPage,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add status filter based on currentTab
      if (currentTab && currentTab !== "All") {
        params.status = currentTab.toUpperCase();
      }

      const response = await getAllReferrals(params);

      if (response.success) {
        setReferrals(response.data);
        setPagination({
          page: response.pagination?.page || pagination.page,
          limit: rowsPerPage,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 1,
        });
      }
    } catch (error) {
      console.error("Error fetching referrals:", error);
      toast.error(error.message || "Failed to fetch referrals");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Fetch stats on mount and tab change
  useEffect(() => {
    fetchStats();
  }, [currentTab]);

  // Fetch data on page, tab, search, or rowsPerPage change
  useEffect(() => {
    fetchReferrals();
  }, [pagination.page, currentTab, searchQuery, rowsPerPage]);

  // Handle view details (read-only)
  const handleViewClick = (referral) => {
    setSelectedReferral(referral);
    setViewDialog(true);
  };

  // Handle edit/process payment (only for pending requests)
  const handleProcessClick = (referral) => {
    if (referral.status !== "PENDING") {
      toast.warning("Only pending requests can be processed");
      return;
    }
    setSelectedReferral(referral);
    setProcessStatus("");
    setPayAmount(referral.requestAmount || "");
    setTransactionId("");
    setScreenShotFiles([]);
    setProcessDialog(true);
  };

  // Handle delete
  const handleDeleteClick = (referral) => {
    setSelectedReferral(referral);
    setDeleteDialog(true);
  };

  // Process payment request
  const handleProcessPayment = async () => {
    try {
      if (!selectedReferral) return;

      // Validation
      if (!processStatus) {
        toast.error("Please select Approved or Declined");
        return;
      }
      if (!payAmount || parseFloat(payAmount) <= 0) {
        toast.error("Please enter valid payment amount");
        return;
      }
      if (processStatus === "Approved" && (!transactionId || transactionId.trim() === "")) {
        toast.error("Transaction ID is required for approval");
        return;
      }
      if (processStatus === "Approved" && (!screenShotFiles || screenShotFiles.length === 0)) {
        toast.error("Screenshot is required for approval");
        return;
      }

      setProcessing(true);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("status", processStatus === "Approved" ? "APPROVED" : "DECLINED");
      formData.append("payAmount", parseFloat(payAmount));

      // Add transactionId and screenshot only for approved status
      if (processStatus === "Approved") {
        formData.append("transactionId", transactionId);

        // Add file if uploaded - FileUploader stores files as {file: File, preview: string}
        if (screenShotFiles.length > 0 && screenShotFiles[0] && screenShotFiles[0].file) {
          console.log(
            "Appending screenshot file:",
            screenShotFiles[0].file.name,
            screenShotFiles[0].file.type,
            screenShotFiles[0].file.size
          );
          formData.append("screenShot", screenShotFiles[0].file);
        } else {
          console.log("No screenshot file found. screenShotFiles:", screenShotFiles);
        }
      }

      console.log("Processing payment with FormData");
      console.log("Selected processStatus:", processStatus);

      const response = await processRedeemRequest(selectedReferral._id, formData);

      console.log("API Response:", response);

      if (response.success) {
        toast.success("Payment processed successfully");
        setProcessDialog(false);
        fetchReferrals();
        resetForm();
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(error.message || "Failed to process payment");
    } finally {
      setProcessing(false);
    }
  };

  // Handle process status change to reset fields
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setProcessStatus(newStatus);
    if (newStatus === "Declined") {
      setTransactionId("");
      setScreenShotFiles([]);
    }
  };
  const handleConfirmDelete = async () => {
    try {
      if (!selectedReferral) return;

      const response = await deleteReferral(selectedReferral._id);

      if (response.success) {
        toast.success("Referral deleted successfully");
        setDeleteDialog(false);
        fetchReferrals();
      }
    } catch (error) {
      console.error("Error deleting referral:", error);
      toast.error(error.message || "Failed to delete referral");
    }
  };

  // Reset form
  const resetForm = () => {
    setProcessStatus("");
    setPayAmount("");
    setTransactionId("");
    setScreenShotFiles([]);
    setSelectedReferral(null);
  };

  // Close dialogs
  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setSelectedReferral(null);
  };

  const handleCloseProcessDialog = () => {
    setProcessDialog(false);
    resetForm();
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog(false);
    setSelectedReferral(null);
  };

  // Author component
  const Author = ({ image, name, email }) => (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar src={image} alt={name} size="sm" variant="full" />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography variant="button" fontWeight="medium">
          {name}
        </ArgonTypography>
        <ArgonTypography variant="caption" color="secondary">
          {email}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );

  // PropTypes validation for Author component
  Author.propTypes = {
    image: PropTypes.string,
    name: PropTypes.string.isRequired,
    email: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  // Generate table columns
  const columns = [
    { name: "sno", align: "center" },
    { name: "Name(Mobile)", align: "left" },
    { name: "Locked Earnings", align: "left" },
    { name: "Referral wallet", align: "left" },
    { name: "Referral income", align: "left" },
    { name: "Invited referral", align: "left" },
    { name: "request date", align: "left" },
    { name: "request amount", align: "center" },
    { name: "pay amount", align: "center" },
    { name: "pay date", align: "center" },
    { name: "status", align: "center" },
    { name: "screen shot", align: "center" },
    { name: "transaction id", align: "center" },
    { name: "action", align: "left" },
  ];

  // Generate table rows from API data
  const rows = referrals.map((referral, index) => {
    const user = referral.tutorId || referral.parentId;
    const userName = user?.fullName || "N/A";
    const userPhone = user?.phone || "N/A";
    const userImage = user?.profileImage || "";
    const serialNumber = (pagination.page - 1) * rowsPerPage + index + 1;

    return {
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {serialNumber}
        </ArgonTypography>
      ),
      "Name(Mobile)": <Author image={userImage} name={userName} email={userPhone} />,
      "Referral wallet": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹{referral.referralWallet || 0}
        </ArgonTypography>
      ),
      "Referral income": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹{referral.referralIncome || 0}
        </ArgonTypography>
      ),
      "Locked Earnings": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
         ₹{referral.lockedReferralEarnings || 0}
        </ArgonTypography>
      ),
      "Invited referral": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {referral.invitedReferralCount || 0}
        </ArgonTypography>
      ),
      "request date": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {referral.requestDate ? new Date(referral.requestDate).toLocaleDateString() : "N/A"}
        </ArgonTypography>
      ),
      "request amount": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹{referral.requestAmount || 0}
        </ArgonTypography>
      ),
      "pay amount": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          ₹{referral.payAmount || 0}
        </ArgonTypography>
      ),
      "pay date": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {referral.payDate ? new Date(referral.payDate).toLocaleDateString() : "N/A"}
        </ArgonTypography>
      ),
      status: (
        <ArgonBadge
          variant="gradient"
          badgeContent={referral.status || "N/A"}
          color={
            referral.status === "APPROVED"
              ? "success"
              : referral.status === "PENDING"
              ? "warning"
              : referral.status === "DECLINED"
              ? "error"
              : "secondary"
          }
          size="xs"
          container
        />
      ),
      "screen shot": referral.screenShot ? (
        <ArgonTypography
          component="a"
          href={referral.screenShot}
          target="_blank"
          variant="caption"
          color="primary"
          fontWeight="medium"
        >
          View
        </ArgonTypography>
      ) : (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          N/A
        </ArgonTypography>
      ),
      "transaction id": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {referral.transactionId || "N/A"}
        </ArgonTypography>
      ),
      action: (
        <ArgonBox display="flex" alignItems="center" gap={0}>
          {/* Edit Icon - Only for PENDING status */}
          {referral.status === "PENDING" && (
            <Button
              onClick={() => handleProcessClick(referral)}
              variant="text"
              size="large"
              sx={{ minWidth: "32px", p: 0 }}
              title="Process Payment"
            >
              <EditIcon color="info" />
            </Button>
          )}

          {/* View Icon - Always visible */}
          <Button
            onClick={() => handleViewClick(referral)}
            variant="text"
            size="large"
            sx={{ minWidth: "12px", p: 0 }}
            title="View Details"
          >
            <VisibilityIcon color="primary" />
          </Button>

          {/* Delete Icon */}
          <Button
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => handleDeleteClick(referral)}
            title="Delete Referral"
          >
            <DeleteIcon color="error" />
          </Button>
        </ArgonBox>
      ),
    };
  });

  // Tab data with counts
  const tabsData = [
    { label: "All", count: stats.total, color: "#2027e1ff", content: "" },
    { label: "Pending", count: stats.pending, color: "#ec3c10ff", content: "" },
    { label: "Approved", count: stats.approved, color: "#ec10c7ff", content: "" },
    { label: "Declined", count: stats.declined, color: "#10a6ecff", content: "" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                All Referral
              </ArgonTypography>

              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs
                    tabsData={tabsData}
                    onTabChange={(tab) => {
                      setCurrentTab(tab);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    activeTab={currentTab}
                  />
                </ArgonBox>

                <ArgonBox
                  sx={{ height: "36px" }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ArgonInput
                    placeholder="Search by name, phone, referral code..."
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching referrals...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : rows.length === 0 ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {currentTab === "All"
                      ? "No Referral found"
                      : `No ${currentTab.toLowerCase()} referrals found`}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {currentTab === "All"
                      ? "Try adjusting your filters or check back later."
                      : `No referrals with ${currentTab.toLowerCase()} status found.`}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>

            {pagination.total > 0 && !loading && (
              <PaginationBar
                currentPage={pagination.page}
                rowsPerPage={rowsPerPage}
                totalItems={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>

        {/* View Details Dialog (Read-Only) */}
        <Dialog
          open={viewDialog}
          onClose={handleCloseViewDialog}
          maxWidth="md"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
              backgroundColor: "#fff",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fff",
            }}
          >
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="bold" color="text">
                  Referral Details
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text.secondary">
                  Complete information about this referral
                </ArgonTypography>
              </ArgonBox>
              <ArgonBox display="flex" alignItems="center" gap={1}>
                {selectedReferral?.status === "PENDING" && (
                  <ArgonButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={() => {
                      handleCloseViewDialog();
                      handleProcessClick(selectedReferral);
                    }}
                  >
                    Process Payment
                  </ArgonButton>
                )}
                <Button
                  onClick={handleCloseViewDialog}
                  variant="text"
                  size="small"
                  sx={{
                    minWidth: "32px",
                    width: "32px",
                    height: "32px",
                    p: 0,
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  title="Close"
                >
                  <CloseIcon fontSize="small" />
                </Button>
              </ArgonBox>
            </ArgonBox>
          </DialogTitle>

          <DialogContent sx={{ p: 3, backgroundColor: "#fff" }}>
            <Grid container spacing={3}>
              {/* User Info */}
              <Grid item xs={12}>
                <ArgonBox
                  sx={{
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <ArgonTypography variant="subtitle2" fontWeight="bold" mb={2} color="dark">
                    User Information
                  </ArgonTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Name
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium">
                        {selectedReferral?.tutorId?.fullName ||
                          selectedReferral?.parentId?.fullName ||
                          "N/A"}
                      </ArgonTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Mobile
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium">
                        {selectedReferral?.tutorId?.phone ||
                          selectedReferral?.parentId?.phone ||
                          "N/A"}
                      </ArgonTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        User Type
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium">
                        {selectedReferral?.userType || "N/A"}
                      </ArgonTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Referral Code
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium" color="info">
                        {selectedReferral?.referralCode || "N/A"}
                      </ArgonTypography>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </Grid>

              {/* Wallet Info */}
              <Grid item xs={12}>
                <ArgonBox
                  sx={{
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <ArgonTypography variant="subtitle2" fontWeight="bold" mb={2} color="dark">
                    Wallet & Earnings
                  </ArgonTypography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Referral Wallet
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="success">
                        ₹{selectedReferral?.referralWallet || 0}
                      </ArgonTypography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Referral Income
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="info">
                        ₹{selectedReferral?.referralIncome || 0}
                      </ArgonTypography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Locked Earnings
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="warning">
                        ₹{selectedReferral?.lockedReferralEarnings || selectedReferral?.lockedReferralAmount || 0}
                      </ArgonTypography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <ArgonTypography variant="caption" color="text.secondary">
                        Invited Referrals
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="primary">
                        {selectedReferral?.invitedReferralCount || 0}
                      </ArgonTypography>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </Grid>

              {/* Current Request Info */}
              {selectedReferral?.status && (
                <Grid item xs={12}>
                  <ArgonBox
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "12px",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <ArgonTypography variant="subtitle2" fontWeight="bold" mb={2} color="dark">
                      Current Redeem Request
                    </ArgonTypography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <ArgonTypography variant="caption" color="text.secondary">
                          Request Date
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {selectedReferral?.requestDate
                            ? new Date(selectedReferral.requestDate).toLocaleDateString()
                            : "N/A"}
                        </ArgonTypography>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonTypography variant="caption" color="text.secondary">
                          Request Amount
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          ₹{selectedReferral?.requestAmount || 0}
                        </ArgonTypography>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonTypography variant="caption" color="text.secondary">
                          Status
                        </ArgonTypography>
                        <ArgonBadge
                          variant="gradient"
                          badgeContent={selectedReferral?.status || "N/A"}
                          color={
                            selectedReferral?.status === "APPROVED"
                              ? "success"
                              : selectedReferral?.status === "PENDING"
                              ? "warning"
                              : "error"
                          }
                          size="sm"
                        />
                      </Grid>
                      {selectedReferral?.status === "APPROVED" && (
                        <>
                          <Grid item xs={4}>
                            <ArgonTypography variant="caption" color="text.secondary">
                              Pay Amount
                            </ArgonTypography>
                            <ArgonTypography variant="body2" fontWeight="medium" color="success">
                              ₹{selectedReferral?.payAmount || 0}
                            </ArgonTypography>
                          </Grid>
                          <Grid item xs={4}>
                            <ArgonTypography variant="caption" color="text.secondary">
                              Pay Date
                            </ArgonTypography>
                            <ArgonTypography variant="body2" fontWeight="medium">
                              {selectedReferral?.payDate
                                ? new Date(selectedReferral.payDate).toLocaleDateString()
                                : "N/A"}
                            </ArgonTypography>
                          </Grid>
                          <Grid item xs={4}>
                            <ArgonTypography variant="caption" color="text.secondary">
                              Transaction ID
                            </ArgonTypography>
                            <ArgonTypography variant="body2" fontWeight="medium">
                              {selectedReferral?.transactionId || "N/A"}
                            </ArgonTypography>
                          </Grid>
                          {selectedReferral?.screenShot && (
                            <Grid item xs={12}>
                              <ArgonTypography variant="caption" color="text.secondary">
                                Payment Screenshot
                              </ArgonTypography>
                              <ArgonButton
                                component="a"
                                href={selectedReferral.screenShot}
                                target="_blank"
                                variant="outlined"
                                color="info"
                                size="small"
                                fullWidth
                                sx={{ mt: 1 }}
                              >
                                View Screenshot
                              </ArgonButton>
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </ArgonBox>
                </Grid>
              )}

              {/* Redeem History */}
              {selectedReferral?.redeemHistory && selectedReferral.redeemHistory.length > 0 && (
                <Grid item xs={12}>
                  <ArgonBox
                    sx={{
                      p: 2,
                      backgroundColor: "#f8f9fa",
                      borderRadius: "12px",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <ArgonTypography variant="subtitle2" fontWeight="bold" mb={2} color="dark">
                      Redeem History ({selectedReferral.redeemHistory.length})
                    </ArgonTypography>
                    <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto" }}>
                      {selectedReferral.redeemHistory.map((history, index) => (
                        <ArgonBox
                          key={index}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            border: "1px solid #dee2e6",
                          }}
                        >
                          <Grid container spacing={1}>
                            <Grid item xs={2.4}>
                              <ArgonTypography variant="caption" color="text.secondary">
                                Date
                              </ArgonTypography>
                              <ArgonTypography
                                variant="caption"
                                fontWeight="medium"
                                display="block"
                              >
                                {history.requestDate
                                  ? new Date(history.requestDate).toLocaleDateString()
                                  : "N/A"}
                              </ArgonTypography>
                            </Grid>
                            <Grid item xs={2.4}>
                              <ArgonTypography variant="caption" color="text.secondary">
                                Amount
                              </ArgonTypography>
                              <ArgonTypography
                                variant="caption"
                                fontWeight="medium"
                                display="block"
                              >
                                ₹{history.amount || 0}
                              </ArgonTypography>
                            </Grid>
                            <Grid item xs={2.4}>
                              <ArgonTypography variant="caption" color="text.secondary">
                                Status
                              </ArgonTypography>
                              <ArgonBadge
                                variant="contained"
                                badgeContent={history.status || "N/A"}
                                color={
                                  history.status === "APPROVED"
                                    ? "success"
                                    : history.status === "PENDING"
                                    ? "warning"
                                    : "error"
                                }
                                size="xs"
                              />
                            </Grid>
                            <Grid item xs={2.4}>
                              <ArgonTypography variant="caption" color="text.secondary">
                                TXN ID
                              </ArgonTypography>
                              <ArgonTypography
                                variant="caption"
                                fontWeight="medium"
                                display="block"
                              >
                                {history.transactionId || "N/A"}
                              </ArgonTypography>
                            </Grid>
                            <Grid item xs={2.4}>
                              <ArgonTypography variant="caption" color="text.secondary">
                                Screenshot
                              </ArgonTypography>
                              {history.screenShot ? (
                                <ArgonTypography
                                  component="a"
                                  href={history.screenShot}
                                  target="_blank"
                                  variant="caption"
                                  color="primary"
                                  fontWeight="medium"
                                  display="block"
                                >
                                  View
                                </ArgonTypography>
                              ) : (
                                <ArgonTypography
                                  variant="caption"
                                  fontWeight="medium"
                                  display="block"
                                  color="text.secondary"
                                >
                                  No Screenshot
                                </ArgonTypography>
                              )}
                            </Grid>
                          </Grid>
                        </ArgonBox>
                      ))}
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2,
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fff",
            }}
          >
            <ArgonButton variant="outlined" onClick={handleCloseViewDialog} color="primary">
              Close
            </ArgonButton>
          </DialogActions>
        </Dialog>

        {/* Process Payment Dialog (Edit Mode - Only for Pending) */}
        <Dialog
          open={processDialog}
          onClose={handleCloseProcessDialog}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "16px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
              backgroundColor: "#fff",
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fff",
            }}
          >
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
              <ArgonBox>
                <ArgonTypography variant="h6" fontWeight="bold" color="text">
                  Process Payment -{" "}
                  {selectedReferral?.tutorId?.fullName ||
                    selectedReferral?.parentId?.fullName ||
                    "User"}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text.secondary">
                  Approve or decline referral payment request of ₹
                  {selectedReferral?.requestAmount || 0}
                </ArgonTypography>
              </ArgonBox>
              <Button
                onClick={handleCloseProcessDialog}
                variant="text"
                size="small"
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  p: 0,
                  borderRadius: "50%",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
                title="Close"
              >
                <CloseIcon fontSize="small" />
              </Button>
            </ArgonBox>
          </DialogTitle>

          <DialogContent sx={{ p: 3, backgroundColor: "#fff" }}>
            <form>
              <Grid container spacing={3}>
                {/* Request Amount (disabled) */}
                <Grid item xs={12}>
                  <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                    Request Amount
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    disabled
                    value={`₹ ${selectedReferral?.requestAmount || 0}`}
                  />
                </Grid>

                {/* Approved / Declined */}
                <Grid item xs={12}>
                  <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                    Approved/Declined <span style={{ color: "red" }}>*</span>
                  </ArgonTypography>
                  <select
                    value={processStatus}
                    onChange={handleStatusChange}
                    style={{
                      width: "100%",
                      minHeight: "42px",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid #d2d6da",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      outline: "none",
                      color: processStatus ? "#495057" : "#7b809a",
                      fontWeight: "400",
                      fontSize: "0.875rem",
                      transition: "border-color 0.15s ease-in-out",
                    }}
                  >
                    <option value="" disabled>
                      Select Status
                    </option>
                    <option value="Approved">Approved</option>
                    <option value="Declined">Declined</option>
                  </select>
                </Grid>

                {/* Payment Amount */}
                <Grid item xs={12}>
                  <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                    Payment Amount <span style={{ color: "red" }}>*</span>
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    placeholder="Enter actual payment amount"
                    type="number"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    required
                  />
                </Grid>

                {/* Payment Screenshot - Only for Approved */}
                {processStatus === "Approved" && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                      Payment Screenshot <span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <ArgonBox
                      sx={{
                        border: "2px dashed #d0d7de",
                        borderRadius: "12px",
                        p: 1,
                        textAlign: "center",
                        backgroundColor: "#fafafa",
                        cursor: "pointer",
                        transition: "0.3s",
                        "&:hover": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <ArgonBox>
                        <FileUploader
                          files={screenShotFiles}
                          setFiles={setScreenShotFiles}
                          accept="image/*,application/pdf"
                          multiple={false}
                          Width="100%"
                          Height={90}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  </Grid>
                )}

                {/* Transaction ID - Only for Approved */}
                {processStatus === "Approved" && (
                  <Grid item xs={12}>
                    <ArgonTypography variant="body2" fontWeight="bold" mb={1}>
                      Transaction ID <span style={{ color: "red" }}>*</span>
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      placeholder="Enter transaction reference ID"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      required
                    />
                  </Grid>
                )}
              </Grid>
            </form>
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              gap: 2,
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#fff",
            }}
          >
            <ArgonButton
              variant="outlined"
              onClick={handleCloseProcessDialog}
              color="primary"
              disabled={processing}
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="primary"
              onClick={handleProcessPayment}
              disabled={processing}
            >
              {processing ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                "Process Payment"
              )}
            </ArgonButton>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <DeleteDialog
          open={deleteDialog}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          title="Delete Referral"
          description="Are you sure you want to delete this referral?"
          confirmText="Delete"
          cancelText="Cancel"
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
